import {eratoDataFetchResponseValidator} from "~/components/reports/widgetComponent";
import {robustClientFetch} from "@vesta/error-handling-remix/fetches/robustClientFetch";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {jsonValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";
import {fetchMinervaWorkspaceDatasets} from "../analytics/-minervaDatasets";
import {
    datasetZodValidator,
    type Dataset,
    type DatasetMetadata,
    type EratoFilter,
    type EratoOrderBy,
    type EratoRawData,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";

type DataFetchRequest = {
    datasetId: Uuid;
    workspaceId: Uuid;
    userId: Uuid;
    timeRange: {
        startDate: string;
        endDate: string;
        granularity: "Day" | "Month" | "Year";
        timezone: string;
    };
    metrics: Array<{id: Uuid; distributed: boolean}>;
    dimensions: Array<{id: Uuid}>;
    filter?: EratoFilter;
    orderBy?: Array<EratoOrderBy>;
    limit?: number;
};

type DistinctValuesRequest = {
    datasetId: Uuid;
    workspaceId: Uuid;
    attributeId: Uuid;
    limit?: number;
};

type CanonicalDistinctValuesRequest = {
    workspaceId: Uuid;
    canonicalId: Uuid;
    datasetIdList: Array<Uuid>;
    limit?: number;
};

const minervaDatasetDetailsValidator = zod.object({
    agentContext: zod
        .string()
        .nullable()
        .transform((value) => value ?? ""),
    description: zod
        .string()
        .nullable()
        .transform((value) => value ?? ""),
    dimensions: zod.record(zod.string(), zod.unknown()),
    displayName: zod.string(),
    healthStatus: zod.string().uuid(),
    id: zod.string().uuid(),
    metrics: zod.record(zod.string(), zod.unknown()),
});

type DebuggerFetchError = {
    body: unknown;
    bodyText: string;
    error?: unknown;
    status: number | null;
    statusText: string;
    url: string;
    validationError?: unknown;
};

const resultEnvelopeValidator = zod.discriminatedUnion("success", [
    zod.object({
        success: zod.literal(true),
        data: zod.unknown().optional(),
        ok: zod.unknown().optional(),
    }),
    zod.object({
        success: zod.literal(false),
        error: zod.unknown().optional(),
        err: zod.unknown().optional(),
    }),
]);

function parseResponseBody(responseText: string): unknown {
    if (responseText.trim() === "") {
        return null;
    }
    try {
        return JSON.parse(responseText);
    } catch {
        return responseText;
    }
}

async function debuggerJsonResultFetch<T>(
    requestUrl: string,
    requestOptions: RequestInit,
    responseValidator: zod.ZodSchema<T>,
): Promise<Result<T, DebuggerFetchError>> {
    try {
        const response = await fetch(requestUrl, requestOptions);
        const responseText = await response.text();
        const responseBody = parseResponseBody(responseText);

        if (response.ok === false) {
            return errorResult({
                body: responseBody,
                bodyText: responseText,
                status: response.status,
                statusText: response.statusText,
                url: requestUrl,
            });
        }

        const envelopeResult = resultEnvelopeValidator.safeParse(responseBody);
        if (envelopeResult.success === false) {
            return errorResult({
                body: responseBody,
                bodyText: responseText,
                status: response.status,
                statusText: response.statusText,
                url: requestUrl,
                validationError: envelopeResult.error.flatten(),
            });
        }

        const envelope = envelopeResult.data;
        if (envelope.success === false) {
            return errorResult({
                body: responseBody,
                bodyText: responseText,
                error: envelope.error ?? envelope.err,
                status: response.status,
                statusText: response.statusText,
                url: requestUrl,
            });
        }

        const unvalidatedData = envelope.data ?? envelope.ok;
        const validatedDataResult = responseValidator.safeParse(unvalidatedData);
        if (validatedDataResult.success === false) {
            return errorResult({
                body: responseBody,
                bodyText: responseText,
                status: response.status,
                statusText: response.statusText,
                url: requestUrl,
                validationError: validatedDataResult.error.flatten(),
            });
        }

        return dataResult(validatedDataResult.data);
    } catch (caughtError) {
        return errorResult({
            body: null,
            bodyText: "",
            error: caughtError,
            status: null,
            statusText: "Request failed before receiving a response",
            url: requestUrl,
        });
    }
}

export async function fetchDatasets({
    intellsysBaseUrl,
    accessToken,
    workspaceId,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
}): Promise<Result<Array<DatasetMetadata>>> {
    try {
        const datasets = await fetchMinervaWorkspaceDatasets({accessToken, intellsysBaseUrl, workspaceId});
        return dataResult(
            datasets.map((dataset) => ({
                datasetTemplateId: null,
                description: dataset.description,
                displayName: dataset.displayName,
                agentContext: dataset.agentContext,
                healthStatus: dataset.healthStatus as Uuid,
                id: dataset.id as Uuid,
                imageDetails: null,
            })),
        );
    } catch (error) {
        return errorResult(error instanceof Error ? error : new Error("Could not list Minerva datasets"));
    }
}

export async function fetchDatasetDetails({
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    datasetId,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    datasetId: Uuid;
}): Promise<Result<Dataset>> {
    const result = await robustClientFetch(
        `${intellsysBaseUrl}/sub-system/minerva/datasets/${datasetId}`,
        {headers: {Authorization: `Bearer ${accessToken}`}},
        jsonValidator.pipe(minervaDatasetDetailsValidator),
    );
    if (result.success !== true) return errorResult(result.error);

    const parsed = datasetZodValidator.safeParse({
        agentContext: result.data.agentContext,
        datasetId: result.data.id,
        datasetTemplateId: null,
        description: result.data.description,
        dimensions: result.data.dimensions,
        displayName: result.data.displayName,
        healthStatus: result.data.healthStatus,
        metrics: result.data.metrics,
    });
    if (parsed.success !== true) return errorResult(new Error("Minerva returned invalid dataset metadata"));
    return dataResult(parsed.data);
}

export async function fetchData({
    intellsysBaseUrl,
    accessToken,
    request,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: DataFetchRequest;
}): Promise<Result<EratoRawData, DebuggerFetchError>> {
    return debuggerJsonResultFetch(
        `${intellsysBaseUrl}/sub-system/erato/analytics/admin/dataset/get-data`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({...request, limit: request.limit ?? 100}),
        },
        eratoDataFetchResponseValidator,
    );
}

export async function fetchDistinctValues({
    intellsysBaseUrl,
    accessToken,
    request,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: DistinctValuesRequest;
}): Promise<Result<Array<unknown>, DebuggerFetchError>> {
    return debuggerJsonResultFetch(
        `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/distinct-values`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({...request, limit: request.limit ?? 100}),
        },
        zod.array(zod.unknown()),
    );
}

export async function fetchCanonicalDistinctValues({
    intellsysBaseUrl,
    accessToken,
    request,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: CanonicalDistinctValuesRequest;
}): Promise<Result<Array<unknown>, DebuggerFetchError>> {
    return debuggerJsonResultFetch(
        `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/canonical-distinct-values`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({...request, limit: request.limit ?? 100}),
        },
        zod.array(zod.unknown()),
    );
}
